import { Plus, Trash2, TriangleAlert, X } from 'lucide-react'
import { useState } from 'react'

import { SuppressedValue } from '@/components/data/SuppressedValue'
import { Button } from '@/components/ui/Button'
import { RateMemoryChip } from '@/components/ui/Tag'
import {
  CostLineInlineRow,
  type CostLineInlineDraft,
} from '@/features/quotes/components/CostLineInlineRow'
import { cn } from '@/lib/cn'
import { formatNumber } from '@/lib/format'
import type { CostLine, CostTotals } from '@/types/domain'
import { useTranslation } from 'react-i18next'

/* The line-item editor: the visual twin of the dense table for editable rows.
 *
 * This is the surface with the tightest speed budget in the product. The PRD's
 * explicit counter-signal is that quoting must not be slower than the Excel
 * path it replaces, so: editing is one click on the row, adding never opens a
 * modal from a button hunt, and the add-row sits INSIDE the table at data-row
 * height rather than below it.
 *
 * That last clause described an intention, not the code, from the day this
 * shipped until the inline row arrived: Add opened the same sheet as editing.
 * Then adding went inline and editing kept the sheet, which left the faster
 * path on the rarer action — a line is added once and corrected several times.
 * Both are now inline on desktop: clicking a row turns THAT row into the same
 * form, and each row carries its own delete in a trailing column.
 *
 * `onEdit` survives for the phone, and as the desktop's fallback when no inline
 * handler is wired. On phone the pattern changes rather than shrinking — one
 * line, one sheet. Horizontally scrolling an editable grid is prohibited.
 *
 * Deleting is TWO presses on the trailing column, never one: the first arms the
 * row, the second removes it. A one-click delete beside a click-to-edit row is
 * the mis-click that costs a line and its share of the margin, and an inline
 * row has no room for the sheet's sentence of confirmation.
 *
 * FIVE figure columns (S-5, owner decision O5): Qty | Unit cost | Cost |
 * Markup | Client price. The unit cost is the one figure an operator types per
 * charge line; the cost is that figure at the quantity beside it; the markup
 * and the client price are the SERVER's — the quote holds ONE stored markup,
 * and every line's price is its cost carried up by it, derived on each write.
 *
 * The per-line client price used to be hidden, because the markup was not
 * stored: a line edited after Apply fell back to its cost while its neighbours
 * kept their prices (Q-11), so a per-line price was a figure in flux. With the
 * markup stored it is a fact, and the reader asked for it (Q-14).
 *
 * The cost columns are still two, and must be. One column headed "Unit price"
 * printing the extension is a heading that lies about its own cell; one headed
 * "Amount" holding the input puts the figure an operator types under the word
 * for the figure nobody types. The source workbook has both for this reason.
 *
 * A line priced at anything other than the quote's markup is FLAGGED in its
 * markup cell (L10) — a shape and words, never a colour alone. Only a costing
 * priced before the markup was stored can hold one, and the reader must see
 * which line it is before an edit or an Apply moves it.
 *
 * Nothing here computes a client figure (L16). Each line prints `sellTotal`,
 * `markupPercent` and `atQuoteMarkup` as served; the footer prints
 * `totals.markupPercent` and `totals.markupMixed`. A line served without its
 * `sellTotal` shows the empty marker, not a product of figures in hand.
 *
 * The footer's first row is the column totals, each under the column it adds
 * up, below the 2px ink rule — the one heavy line permitted anywhere in the
 * system, because the boundary between what was entered and what was computed
 * must be unmistakable. The second row states the pair the costing is decided
 * on, «Наценка · Маржа» (L19), and nothing else — no margin money, because
 * every money figure in the footer is the sum of a visible column (O5).
 *
 * The client total is `totals.sell`, and MarginPanel prints the same value off
 * the same record — the costing tab mounts both, so the two are worded from
 * ONE key (`quotes.costLines.customerTotal`), which heads the column too.
 *
 * The totals themselves come from the SERVER (FR-1). They are not summed here
 * even though every input is on screen: two surfaces that each add up the same
 * lines will eventually disagree by a rounding rule. `totals` absent means the
 * response predates the costing endpoint, and the footer falls back to summing
 * the cost and price it always summed — never the new markup figures.
 *
 * ── The width it is given (R1) ──────────────────────────────────────────────
 *
 * The table must neither scroll sideways nor clip a column, and the width it
 * is given is the CARD's, not the viewport's: the xl rail narrows the card to
 * 640px on a 1280px screen, while a 1024px screen gives it 738px. A viewport
 * breakpoint cannot know that, so the table and the cards switch on a
 * CONTAINER query on this editor's own box (`@container`, the precedent is
 * PaymentAllocations), at 620px.
 *
 * And the table is laid out FIXED (`table-fixed`): the header's widths are the
 * columns' widths, so no figure, header or chip can push the table past its
 * box — a long description wraps inside its cell instead. The fixed columns
 * add up to 520px (qty 40, the two costs 112 each, markup 80, client price
 * 104, actions 72), which leaves the description at least 100px at the switch
 * and 120px in the rail-narrowed card. To make that room the vendor gave up
 * its column — the one a costing reads least — and is printed under the
 * description, in the same cell, in both the read row and the open one.
 *
 * Below 620px every line is a card and editing opens the sheet: an editable
 * grid never scrolls sideways.
 */

export interface CostLineEditorProps {
  lines: CostLine[]
  /** The server's arithmetic (FR-1). Preferred over anything computed here. */
  totals?: CostTotals
  /** The quote's stored markup (`QuoteDetail.markupPercent`). An open row
   *  shows it read-only in its markup cell — the markup the saved line takes. */
  markupPercent?: string
  /** Sales users see amounts on their own deals; only the vendor is hidden. */
  readOnly?: boolean
  /** Opens the sheet for a line, or for a new one. Owned by the section above
   *  so a failed write can keep the sheet open with the draft intact.
   *
   *  This is the PHONE's path for both actions, and the desktop's fallback for
   *  whichever inline handler is absent. */
  onEdit?: (line: CostLine | 'new') => void
  /** Saves a row entered inline. Absent means the desktop add-row is not
   *  offered and the Add button falls back to opening the sheet. */
  onAddInline?: (draft: CostLineInlineDraft, onSaved: () => void) => void
  /** Saves an existing row edited in place. Absent means a desktop row click
   *  falls back to opening the sheet. */
  onEditInline?: (
    line: CostLine,
    draft: CostLineInlineDraft,
    onSaved: () => void,
  ) => void
  /** Removes a line from the trailing column. Absent means no inline delete is
   *  offered and removal stays in the sheet. */
  onDeleteInline?: (line: CostLine) => void
  /** A write is in flight; the inline row locks rather than accepting a second
   *  line that would race the first. */
  pending?: boolean
}

/** Description (the vendor under it) · Qty · Unit cost · Cost · Markup ·
 *  Client price · actions — the span of a row that crosses the whole table. */
const COLUMN_COUNT = 7

/** The fallback footer, used only when the server sent no totals.
 *
 * Number() on money is exactly what the payload path refuses, and it is
 * tolerable here for the same reason it is not there: this figure is READ, not
 * stored, and a display that is a cent out beats a footer that is blank. The
 * server's totals replace it the moment they arrive. */
function localTotal(lines: CostLine[], side: 'buy' | 'sell'): number {
  return lines.reduce(
    (sum, line) => sum + Number(line[side].amount) * line.quantity,
    0,
  )
}

/** A served percentage at two decimals (O4), or the empty marker when the
 *  server sent none — a line with no cost has no markup to state. */
function percentOrEmpty(value: string | undefined, empty: string): string {
  return value === undefined ? empty : `${formatNumber(value, 2)}%`
}

/** The flag on a line priced at something other than the quote's markup (L10).
 *  A shape and words: the triangle carries it to the eye, the words to a
 *  screen reader and to the pointer's tooltip — colour never says it alone. */
function OffMarkupMarker({ label }: { label: string }) {
  return (
    <span title={label} className="text-attention inline-flex items-center">
      <TriangleAlert size={12} aria-hidden />
      <span className="sr-only">{label}</span>
    </span>
  )
}

export function CostLineEditor({
  lines,
  totals,
  markupPercent,
  readOnly,
  onEdit,
  onAddInline,
  onEditInline,
  onDeleteInline,
  pending,
}: CostLineEditorProps) {
  const { t } = useTranslation()

  /* Whether the desktop add-row is showing. Local because it is pure view
     state: nothing above needs to know, and a failed save keeps the row open
     with what was typed by not clearing it. */
  const [adding, setAdding] = useState(false)

  /* The row being edited in place, and the row armed for deletion — both by id,
     and both local for the same reason as `adding`. Ids rather than the line
     objects so that a refetch between renders cannot leave a stale copy open. */
  const [editingId, setEditingId] = useState<string | null>(null)
  const [armedId, setArmedId] = useState<string | null>(null)

  /* An accepted rate memory (FR-17): the line it was accepted on, and the
     figure to open that line's price field on. Carried as a pair rather than a
     bare amount so it cannot leak onto a different row, and cleared when the
     row closes — a seed that outlived its row would put last quarter's rate
     under the hand of somebody who clicked a line only to read it. */
  const [seed, setSeed] = useState<{ lineId: string; unitPrice: string } | null>(
    null,
  )

  const buy = totals ? Number(totals.buy.amount) : localTotal(lines, 'buy')
  const sell = totals ? Number(totals.sell.amount) : localTotal(lines, 'sell')
  const margin = totals ? Number(totals.margin.amount) : sell - buy
  const marginPercent = totals
    ? Number(totals.marginPercent)
    : sell === 0
      ? 0
      : (margin / sell) * 100

  /* The pair the costing is decided on (L19): the server's effective markup
     beside the margin, «в среднем» when the lines are not all at one markup
     (O6). No fallback for the markup — a response without totals has none to
     state, and averaging the lines here would be client money arithmetic. */
  const empty = t('common.empty')
  const markupAndMargin = t(
    totals?.markupMixed
      ? 'quotes.costLines.markupAndMarginMixed'
      : 'quotes.costLines.markupAndMargin',
    {
      markup: percentOrEmpty(totals?.markupPercent, empty),
      margin: `${formatNumber(marginPercent, 2)}%`,
    },
  )
  const offMarkup = t('quotes.costLines.offMarkup')

  /** Opens a line for editing: in place where the caller wired an inline
   *  handler, in the sheet otherwise. Arming a delete is dropped on the way in,
   *  so a row cannot be open for editing and waiting to be removed at once. */
  const openLine = (line: CostLine) => {
    setArmedId(null)
    /* A seed for THIS line survives, because accepting a rate memory opens the
       row through this same path — the chip sits inside the row and its click
       reaches the row's handler too. A seed for any other line is dropped. */
    setSeed((current) => (current?.lineId === line.id ? current : null))
    if (onEditInline) {
      setEditingId(line.id)
      return
    }
    onEdit?.(line)
  }

  /** Closes an open row and forgets any figure a rate memory seeded it with. */
  const closeLine = () => {
    setEditingId(null)
    setSeed(null)
  }

  /** Accepting a rate memory (FR-17): opens the line's row on the remembered
   *  figure. CURRIED so the amount is read where the DTO is still narrowed and
   *  bound as a value — the handler closes over a string, not over the line. */
  const acceptRate = (lineId: string, unitPrice: string) => () => {
    setArmedId(null)
    setSeed({ lineId, unitPrice })
    setEditingId(lineId)
  }

  /** A line's extended COST, server-computed where it exists. The client side
   *  has no such fallback (L16): it is `sellTotal` or the empty marker. */
  const extendedCost = (line: CostLine): number =>
    line.buyTotal ? Number(line.buyTotal.amount) : Number(line.buy.amount) * line.quantity

  const clientAmount = (line: CostLine): string =>
    line.sellTotal ? formatNumber(line.sellTotal.amount, 2) : empty

  return (
    /* The container the table and the cards switch on — see the header
       comment's R1 note. */
    <div className="@container">
      {/* The editable grid, from a 620px editor. */}
      <div className="hidden @min-[620px]:block">
        <table className="w-full table-fixed border-collapse text-left">
          <thead>
            <tr className="border-line border-b">
              <th scope="col" className="asa-eyebrow h-9 px-[18px]">{t('quotes.costLines.description')}</th>
              <th scope="col" className="asa-eyebrow h-9 w-10 px-2 text-right">{t('quotes.costLines.qty')}</th>
              {/* The two COST columns — the unit cost an operator types and
                  the cost it comes to — then the markup and the client price
                  the server derives from them (O5). The headings may wrap
                  («Себестоимость / за ед.»): a fixed column does not grow to
                  fit its heading. */}
              <th scope="col" className="asa-eyebrow h-9 w-28 px-2 py-1 text-right leading-tight">{t('quotes.costLines.unitPrice')}</th>
              <th scope="col" className="asa-eyebrow h-9 w-28 px-2 py-1 text-right leading-tight">{t('quotes.costLines.amount')}</th>
              <th scope="col" className="asa-eyebrow h-9 w-20 px-2 py-1 text-right leading-tight">{t('quotes.costLines.markup')}</th>
              <th scope="col" className="asa-eyebrow h-9 w-26 px-2 py-1 text-right leading-tight">{t('quotes.costLines.customerTotal')}</th>
              {/* The actions column's header is visually empty — the icons
                  under it say what they do — but it is named for screen
                  readers, as the list tables in this system name theirs. */}
              <th scope="col" className="h-9 w-18 px-1">
                <span className="sr-only">{t('common.actions.label')}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {lines.map((line) =>
              /* The open row REPLACES its read row rather than sitting under
                 it, so the table neither grows nor shows the same line twice. */
              !readOnly && onEditInline && editingId === line.id ? (
                <CostLineInlineRow
                  key={line.id}
                  record={line}
                  quoteMarkupPercent={markupPercent}
                  seedUnitPrice={
                    seed?.lineId === line.id ? seed.unitPrice : undefined
                  }
                  onSubmit={(draft, onSaved) =>
                    onEditInline(line, draft, onSaved)
                  }
                  onCancel={closeLine}
                  pending={pending}
                />
              ) : (
              <tr
                key={line.id}
                onClick={readOnly ? undefined : () => openLine(line)}
                tabIndex={readOnly ? undefined : 0}
                aria-label={readOnly ? undefined : t('quotes.costLines.edit')}
                onKeyDown={
                  readOnly
                    ? undefined
                    : (event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault()
                          openLine(line)
                        }
                      }
                }
                className={cn(
                  'border-line-hair h-[38px] border-b transition-colors',
                  !readOnly && 'hover:bg-surface-hover group cursor-pointer',
                )}
              >
                {/* The description, the vendor under it, and the rate memory
                    chip — all of them WRAP inside the cell (`break-words`, and
                    the chip's own nowrap lifted), because a fixed column does
                    not widen for them and anything that could not wrap would
                    spill over the figures beside it. */}
                <td className="px-[18px] py-1.5">
                  <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5 [&_button]:flex-wrap [&_button]:text-left [&_button]:whitespace-normal">
                    <span className="text-text-heading text-[13px] break-words">
                      {line.description}
                    </span>
                    {/* Rate memory REPORTS; it never pre-fills. Accepting is one
                        click, ignoring costs nothing.
                        The click OPENS the row on the remembered figure rather
                        than writing it: what the lane cost last quarter is a
                        starting point, and the only person who can say whether
                        it still holds is the one looking at this row.
                        Where no inline handler is wired the chip stays mute:
                        the click still reaches the row and opens the sheet,
                        which seeds from the line, so nothing is filled behind
                        the reader's back on that path either. */}
                    {line.rateMemory ? (
                      <RateMemoryChip
                        amount={formatNumber(line.rateMemory.amount.amount, 2)}
                        sourceCaseNumber={line.rateMemory.sourceCaseNumber}
                        month={line.rateMemory.month}
                        onAccept={
                          !readOnly && onEditInline
                            ? // The wire's own string (AD-9), read here and not
                              // inside the handler: Number() on a remembered
                              // rate is how it reaches the field as
                              // 1234.5499999999999.
                              acceptRate(line.id, line.rateMemory.amount.amount)
                            : undefined
                        }
                      />
                    ) : null}
                  </div>
                  {/* The vendor, a second line under the description. A
                      suppressed vendor still says so (AD-6); an absent one
                      prints nothing rather than a dash on every line. */}
                  {line.vendorName ? (
                    <div className="text-text-muted text-[12px] break-words">
                      <SuppressedValue
                        value={line.vendorName}
                        label={t('common.suppressed.vendor')}
                        className="text-[12px]"
                      />
                    </div>
                  ) : null}
                </td>
                <td className="asa-num text-text-body px-2 text-right text-[13px]">
                  {line.quantity}
                </td>
                {/* The UNIT cost: the figure this row's form holds, printed by
                    the read row so that clicking it opens on the number that
                    was already on screen. */}
                <td className="asa-num text-text-body px-2 text-right text-[13px] whitespace-nowrap">
                  {formatNumber(line.buy.amount, 2)}
                </td>
                {/* The EXTENDED cost — what this line costs at the quantity
                    beside it, and the column the footer's cost total adds up. */}
                <td className="asa-num text-text-body px-2 text-right text-[13px] whitespace-nowrap">
                  {formatNumber(extendedCost(line), 2)}
                </td>
                {/* The markup this line is priced at, as served, and the flag
                    when that is not the quote's (L10). */}
                <td className="asa-num text-text-muted px-2 text-right text-[13px] whitespace-nowrap">
                  <span className="inline-flex items-center justify-end gap-1">
                    {line.atQuoteMarkup ? null : <OffMarkupMarker label={offMarkup} />}
                    {percentOrEmpty(line.markupPercent, empty)}
                  </span>
                </td>
                {/* The line's share of what the client pays — the server's
                    `sellTotal`, and the column the client total adds up. It
                    carries the heading colour because it is the figure the
                    line contributes to the price somebody signs. */}
                <td className="asa-num text-text-heading px-2 text-right text-[13px] whitespace-nowrap">
                  {clientAmount(line)}
                </td>

                {/* Delete, in the row, in two presses. The trash is quiet until
                    the row is hovered or focused so a table being READ is a
                    table of figures, not a column of red; the armed state stays
                    visible regardless, because a row waiting for a second press
                    must not vanish when the pointer drifts off it.
                    The armed pair STACKS — the word over the cross — because
                    side by side it is wider than this fixed column. */}
                <td className="px-1">
                  {!readOnly && onDeleteInline ? (
                    <div className="flex items-center justify-end">
                      {armedId === line.id ? (
                        <span className="flex flex-col items-end gap-0.5">
                          <button
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation()
                              onDeleteInline(line)
                              setArmedId(null)
                            }}
                            disabled={pending}
                            aria-label={t('quotes.costLines.deleteConfirmAction')}
                            title={t('quotes.costLines.deleteConfirm')}
                            className="text-negative bg-negative-soft cursor-pointer rounded-[5px] px-1.5 py-0.5 text-[12px] disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            {t('quotes.costLines.deleteConfirmAction')}
                          </button>
                          <button
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation()
                              setArmedId(null)
                            }}
                            aria-label={t('common.actions.cancel')}
                            title={t('common.actions.cancel')}
                            className="text-text-muted hover:bg-surface-hover cursor-pointer rounded-[5px] p-1"
                          >
                            <X size={14} aria-hidden />
                          </button>
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={(event) => {
                            /* The row itself opens the editor on click; the
                               trash must not do both. */
                            event.stopPropagation()
                            setArmedId(line.id)
                          }}
                          disabled={pending}
                          aria-label={t('quotes.costLines.delete')}
                          title={t('quotes.costLines.delete')}
                          className="text-text-muted hover:text-negative hover:bg-surface-hover cursor-pointer rounded-[5px] p-1 opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <Trash2 size={14} aria-hidden />
                        </button>
                      )}
                    </div>
                  ) : null}
                </td>
              </tr>
              ),
            )}

            {/* The add affordance and the row it becomes occupy the same slot,
                so the table does not grow a line when adding starts. */}
            {!readOnly && adding && onAddInline ? (
              <CostLineInlineRow
                quoteMarkupPercent={markupPercent}
                onSubmit={onAddInline}
                onCancel={() => setAdding(false)}
                pending={pending}
              />
            ) : null}

            {!readOnly ? (
              <tr className="border-line-hair h-[38px] border-b">
                <td colSpan={COLUMN_COUNT} className="px-[18px]">
                  <button
                    type="button"
                    onClick={() =>
                      onAddInline ? setAdding(true) : onEdit?.('new')
                    }
                    className="text-brand flex cursor-pointer items-center gap-1.5 text-[13px]"
                  >
                    <Plus size={14} aria-hidden />
                    {t('quotes.costLines.add')}
                  </button>
                </td>
              </tr>
            ) : null}
          </tbody>

          {/* Two rows. The first is the column totals, each under the column it
              adds up (O5): the cost under «Себестоимость», the client price
              under «Цена клиенту». The markup column's cell stays empty — a
              markup is not summed, and its figure is the second row's.
              The second states the pair (L19), «Наценка · Маржа», across the
              row and with no money: every figure in this footer is the sum of
              a visible column, and none adds up to the margin's money.
              Nothing here is summed on this surface — every figure is the
              SERVER's wherever the response carried it (FR-1). */}
          <tfoot>
            {/* The one heavy rule in the system, where the argument turns:
                above it is what was entered, below it what was computed. The
                client total carries the semibold weight — of these figures it
                is the one somebody signs. */}
            <tr className="border-ink border-t-2">
              <td className="text-text-muted px-[18px] pt-2 text-[12.5px]" colSpan={3}>
                {t('quotes.costLines.total')}
              </td>
              <td className="asa-num text-text-heading px-2 pt-2 text-right text-[13px] whitespace-nowrap">
                {formatNumber(buy, 2)}
              </td>
              <td className="px-1" />
              <td className="asa-num text-text-heading px-2 pt-2 text-right text-[14px] font-semibold whitespace-nowrap">
                {formatNumber(sell, 2)}
              </td>
              <td className="px-1" />
            </tr>
            <tr>
              <td
                className={cn(
                  'asa-num px-[18px] pt-1 pb-3 text-right text-[12.5px]',
                  margin < 0 ? 'text-negative' : 'text-text-muted',
                )}
                colSpan={COLUMN_COUNT}
              >
                {markupAndMargin}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Below a 620px editor: one line, one card; tapping opens the sheet. */}
      <div className="@min-[620px]:hidden">
        <ul className="flex flex-col">
          {lines.map((line, index) => {
            /* The description has the card's first line to itself and WRAPS:
               at 390px it used to share the line with the amount and was cut
               to «Trans…», and lines that cannot be told apart cannot be
               checked. Under it the quantity on the left, in one piece, and
               the client amount on the right — FIRST of the money (O5), what
               this line adds to the price the client pays — with the cost and
               the markup it came from beneath it in small type, flagged when
               the line is not at the quote's markup (L10). */
            const body = (
              <>
                <span className="text-text-heading block text-[13px] break-words">
                  {line.description}
                </span>
                <span className="mt-1 flex items-baseline justify-between gap-3">
                  <span className="text-text-muted text-[12px] font-light whitespace-nowrap">
                    {t('quotes.costLines.qty')} {line.quantity}
                  </span>
                  <span className="asa-num text-text-heading text-[13px] whitespace-nowrap">
                    {clientAmount(line)}
                  </span>
                </span>
                <span className="asa-num text-text-muted mt-0.5 flex items-center justify-end gap-1 text-right text-[12px] font-light">
                  {line.atQuoteMarkup ? null : <OffMarkupMarker label={offMarkup} />}
                  <span>
                    {t('quotes.costLines.costAndMarkup', {
                      cost: formatNumber(extendedCost(line), 2),
                      markup: percentOrEmpty(line.markupPercent, empty),
                    })}
                  </span>
                </span>
              </>
            )
            const frame = cn(
              'block w-full px-4 py-3 text-left',
              index !== lines.length - 1 && 'border-line-hair border-b',
            )

            return (
              <li key={line.id}>
                {/* A read-only costing (a sent or converted quote) has nothing
                    to open, so its card is not a button: a control that does
                    nothing when pressed is a control that lies. */}
                {readOnly ? (
                  <div className={frame}>{body}</div>
                ) : (
                  <button type="button" onClick={() => onEdit?.(line)} className={frame}>
                    {body}
                  </button>
                )}
              </li>
            )
          })}
        </ul>

        {/* Both totals (O5), the server's: the cost the cards' small figures
            add up to, and the client price their large figures add up to —
            then the pair, as on the desktop. */}
        <div className="border-ink flex flex-col gap-1 border-t-2 px-4 py-3">
          <dl className="flex flex-col gap-1">
            <div className="flex items-center justify-between">
              <dt className="text-text-muted text-[12.5px]">{t('quotes.costLines.costTotal')}</dt>
              <dd className="asa-num text-text-body text-[13px]">{formatNumber(buy, 2)}</dd>
            </div>
            <div className="flex items-center justify-between">
              <dt className="text-text-muted text-[12.5px]">{t('quotes.costLines.customerTotal')}</dt>
              <dd className="asa-num text-text-heading text-[15px] font-semibold">
                {formatNumber(sell, 2)}
              </dd>
            </div>
          </dl>
          <p
            className={cn(
              'asa-num text-right text-[12px]',
              margin < 0 ? 'text-negative' : 'text-text-muted',
            )}
          >
            {markupAndMargin}
          </p>
        </div>

        {!readOnly ? (
          <div className="px-4 pb-4">
            <Button
              variant="secondary"
              size="sm"
              iconLeft={Plus}
              className="w-full"
              onClick={() => onEdit?.('new')}
            >
              {t('quotes.costLines.add')}
            </Button>
          </div>
        ) : null}
      </div>

      {/* Q-12 / P0-4 of the UX walkthrough. The offer prints ONE line, the
          freight rate at the customer total (`PrintedRevisionLines`), so the
          order of these lines decides nothing the client reads. Said once,
          outside both breakpoints, because a costing that looks like an
          itemised offer is what made Pricing worry about which line came
          first. */}
      {lines.length > 0 ? (
        <p className="text-text-muted px-4 pb-3 text-[12.5px] @min-[620px]:px-[18px]">
          {t('quotes.costLines.offerPrintsOneLine')}
        </p>
      ) : null}
    </div>
  )
}
